import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, Package, Warehouse, Truck, FileText, Receipt,
  DollarSign, UserCog, FolderKanban, Shield, BarChart3, Settings, LogOut,
  ChevronLeft, ChevronRight, Search, Bell, Menu, X, Zap, Bot, Camera,
  Sparkles, Calendar, MapPin, FolderOpen, Smartphone, BarChart2, BookOpen, Navigation,
  Tag, Layers, BoxSelect, ShoppingCart, MessageSquare
} from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { ToastContainer } from './Toast';
import { ErrorBoundary } from './UIComponents';
import { useAIStore } from '../../ai/aiStore';

// ─── Nav configuration (module-level, computed once) ───────────────────────

const navItems = [
  // Core
  { label: 'Dashboard',       path: '/admin/dashboard',       icon: LayoutDashboard, category: 'Core',      end: true  },
  { label: 'Orders',          path: '/admin/orders',          icon: ShoppingCart,    category: 'Core',      end: false },
  { label: 'Customers',       path: '/admin/customers',       icon: Users,           category: 'Core',      end: false },
  { label: 'Products',        path: '/admin/products',        icon: Package,         category: 'Core',      end: false },
  // Catalog Management (NEW)
  { label: 'Product Catalog', path: '/admin/catalog',         icon: BoxSelect,       category: 'Catalog',   end: false },
  { label: 'Brands',          path: '/admin/brands',          icon: Tag,             category: 'Catalog',   end: false },
  { label: 'Categories',      path: '/admin/categories',      icon: Layers,          category: 'Catalog',   end: false },
  { label: 'CCTV Packages',   path: '/admin/packages',        icon: Camera,          category: 'Catalog',   end: false },
  // Operations
  { label: 'Inventory',       path: '/admin/inventory',       icon: Warehouse,       category: 'Operations', end: false },
  { label: 'Suppliers',       path: '/admin/suppliers',       icon: Truck,           category: 'Operations', end: false },
  { label: 'Staff',           path: '/admin/staff',           icon: UserCog,         category: 'Operations', end: false },
  { label: 'Projects',        path: '/admin/projects',        icon: FolderKanban,    category: 'Operations', end: false },
  { label: 'Warranty',        path: '/admin/warranty',        icon: Shield,          category: 'Operations', end: false },
  // Sales & Finance
  { label: 'Quotations',      path: '/admin/quotations',      icon: FileText,        category: 'Sales',     end: false },
  { label: 'Invoices',        path: '/admin/invoices',        icon: Receipt,         category: 'Sales',     end: false },
  { label: 'Finance',         path: '/admin/finance',         icon: DollarSign,      category: 'Sales',     end: false },
  // Analytics
  { label: 'Reports',         path: '/admin/reports',         icon: BarChart3,       category: 'Analytics', end: false },
  { label: 'Analytics',       path: '/admin/analytics',       icon: BarChart2,       category: 'Analytics', end: false },
  // AI Features
  { label: 'AI Assistant',    path: '/admin/ai-assistant',    icon: Bot,             category: 'AI',        end: false },
  { label: 'AI Conversations',path: '/admin/ai-conversations',icon: MessageSquare,   category: 'AI',        end: false },
  { label: 'Camera Planner',  path: '/admin/ai-camera',       icon: Zap,             category: 'AI',        end: false },
  { label: 'AI Recommend',    path: '/admin/ai-recommend',    icon: Sparkles,        category: 'AI',        end: false },
  // Operations+
  { label: 'Bookings',        path: '/admin/bookings',        icon: BookOpen,        category: 'Ops+',      end: false },
  { label: 'Calendar',        path: '/admin/calendar',        icon: Calendar,        category: 'Ops+',      end: false },
  { label: 'GPS Tracking',    path: '/admin/gps',             icon: Navigation,      category: 'Ops+',      end: false },
  // Files & Comms
  { label: 'Files',           path: '/admin/files',           icon: FolderOpen,      category: 'Files',     end: false },
  { label: 'Notifications',   path: '/admin/notifications',   icon: Bell,            category: 'Files',     end: false },
  // Portals
  { label: 'Customer Portal', path: '/admin/customer-portal', icon: Users,           category: 'Portals',   end: false },
  { label: 'Mobile Apps',     path: '/admin/mobile-apps',     icon: Smartphone,      category: 'Portals',   end: false },
  // System
  { label: 'Settings',        path: '/admin/settings',        icon: Settings,        category: 'System',    end: false },
];

const categoryOrder = ['Core', 'Catalog', 'Operations', 'Sales', 'Analytics', 'AI', 'Ops+', 'Files', 'Portals', 'System'];
const grouped = categoryOrder
  .map((cat) => ({ category: cat, items: navItems.filter((item) => item.category === cat) }))
  .filter((g) => g.items.length > 0);

const categoryColors: Record<string, string> = {
  Core:       'text-gray-400',
  Catalog:    'text-[#7FA86A]',
  Operations: 'text-gray-400',
  Sales:      'text-emerald-500',
  Analytics:  'text-blue-500',
  AI:         'text-violet-500',
  'Ops+':     'text-teal-500',
  Files:      'text-amber-500',
  Portals:    'text-indigo-500',
  System:     'text-gray-400',
};

// ─── Scroll position helper ─────────────────────────────────────────────────

const SCROLL_KEY = 'ktec-scroll';

function saveScroll(pathname: string, top: number) {
  try {
    const store = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || '{}');
    store[pathname] = top;
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify(store));
  } catch { /* ignore */ }
}

function restoreScroll(pathname: string): number {
  try {
    const store = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || '{}');
    return store[pathname] ?? 0;
  } catch { return 0; }
}

// ─── Sidebar nav (standalone component, NOT defined inside AdminLayout) ────

interface SidebarNavProps {
  sidebarCollapsed: boolean;
  onNavigate?: () => void; // called after a nav link is clicked (e.g. close mobile drawer)
}

function SidebarNav({ sidebarCollapsed, onNavigate }: SidebarNavProps) {
  const { getUnreadAINotificationCount } = useAIStore();
  const unreadAI = getUnreadAINotificationCount();
  return (
    <nav className="flex-1 overflow-y-auto py-3 px-3" aria-label="Admin navigation">
      {grouped.map((group) => (
        <div key={group.category} className="mb-4">
          {!sidebarCollapsed && (
            <div
              className={`px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1 ${
                categoryColors[group.category] || 'text-gray-400'
              }`}
            >
              {group.category === 'Ops+' ? 'Operations+' : group.category}
              {group.category === 'AI' && (
                <span className="text-[8px] bg-violet-100 text-violet-600 px-1 py-0.5 rounded-full font-bold">
                  NEW
                </span>
              )}
            </div>
          )}
          {group.items.map((item) => {
            const Icon = item.icon;
            const isAI = item.category === 'AI';
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                aria-label={sidebarCollapsed ? item.label : undefined}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 transition-all group relative ${
                    isActive
                      ? isAI
                        ? 'bg-violet-50 text-violet-700 font-medium'
                        : 'bg-[#7FA86A]/10 text-[#7FA86A] font-medium'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      className={`w-4 h-4 flex-shrink-0 transition-colors ${
                        isActive ? (isAI ? 'text-violet-600' : 'text-[#7FA86A]') : ''
                      }`}
                      aria-hidden="true"
                    />
                    {!sidebarCollapsed && (
                      <span className="text-[13px] truncate">{item.label}</span>
                    )}
                    {!sidebarCollapsed && item.path === '/admin/ai-conversations' && unreadAI > 0 && (
                      <span className="ml-auto text-[10px] bg-[#7FA86A] text-white rounded-full px-1.5 py-0.5 font-bold min-w-[18px] text-center">
                        {unreadAI > 9 ? '9+' : unreadAI}
                      </span>
                    )}
                    {sidebarCollapsed && (
                      <span
                        className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none"
                        role="tooltip"
                      >
                        {item.label}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

// ─── AdminLayout ─────────────────────────────────────────────────────────────

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { sidebarCollapsed, toggleSidebar, logout, currentUser } = useAdminStore();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const prevPathRef = useRef(location.pathname);

  // ── Close mobile sidebar on route change ───────────────────────────────
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // ── Scroll position preservation ───────────────────────────────────────
  // Save the scroll position for the previous pathname before we move away.
  useEffect(() => {
    const prev = prevPathRef.current;
    if (prev !== location.pathname) {
      // Save previous page scroll
      if (mainRef.current) {
        saveScroll(prev, mainRef.current.scrollTop);
      }
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Restore scroll for the new pathname after the component has rendered.
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    // Small rAF to let the new page content render first
    const raf = requestAnimationFrame(() => {
      main.scrollTop = restoreScroll(location.pathname);
    });
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/ktec-gate-access');
  }, [logout, navigate]);

  const filteredSearch = navItems.filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ── Shared sidebar header (logo + collapse toggle) ─────────────────────
  const SidebarHeader = () => (
    <div className="flex items-center gap-3 px-5 h-16 border-b border-gray-100 flex-shrink-0">
      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#7FA86A] to-[#96BE82] flex items-center justify-center flex-shrink-0">
        <Zap className="w-4 h-4 text-white" aria-hidden="true" />
      </div>
      {!sidebarCollapsed && (
        <div className="overflow-hidden">
          <div className="text-[13px] font-semibold text-gray-900 tracking-tight">K-Tec Enterprise</div>
          <div className="text-[10px] text-gray-400 font-medium tracking-wide uppercase">Admin Portal</div>
        </div>
      )}
    </div>
  );

  // ── Shared sidebar footer (user info) ──────────────────────────────────
  const SidebarFooter = () => (
    <div className="border-t border-gray-100 p-3 flex-shrink-0">
      <div className="flex items-center gap-3 px-2">
        <div
          className="w-8 h-8 rounded-xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
          aria-hidden="true"
        >
          {(currentUser || 'A').charAt(0).toUpperCase()}
        </div>
        {!sidebarCollapsed && (
          <>
            <div className="flex-1 overflow-hidden">
              <div className="text-[13px] font-medium text-gray-900 truncate">{currentUser || 'Admin'}</div>
              <div className="text-[10px] text-gray-400">Super Admin</div>
            </div>
            <button
              onClick={handleLogout}
              aria-label="Log out"
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#FAFAFA] overflow-hidden font-['Inter',_system-ui,_sans-serif]">
      {/* Toast notifications */}
      <ToastContainer />

      {/* ── Desktop Sidebar ── */}
      <aside
        className={`hidden lg:flex flex-col border-r border-gray-200/80 bg-white/80 backdrop-blur-xl transition-all duration-300 ${
          sidebarCollapsed ? 'w-[72px]' : 'w-[240px]'
        } relative flex-shrink-0`}
        aria-label="Sidebar"
      >
        <SidebarHeader />
        <SidebarNav sidebarCollapsed={sidebarCollapsed} />
        <SidebarFooter />

        {/* Collapse / expand toggle */}
        <button
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute top-16 -right-3 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow z-10"
        >
          {sidebarCollapsed
            ? <ChevronRight className="w-3 h-3 text-gray-500" aria-hidden="true" />
            : <ChevronLeft className="w-3 h-3 text-gray-500" aria-hidden="true" />
          }
        </button>
      </aside>

      {/* ── Mobile Sidebar ── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
              aria-hidden="true"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed left-0 top-0 bottom-0 w-[260px] bg-white z-40 lg:hidden flex flex-col border-r border-gray-200"
              aria-label="Mobile navigation"
            >
              <div className="flex items-center justify-between px-5 h-16 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#7FA86A] to-[#96BE82] flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-gray-900 tracking-tight">K-Tec Enterprise</div>
                    <div className="text-[10px] text-gray-400 font-medium tracking-wide uppercase">Admin Portal</div>
                  </div>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close mobile menu"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
              {/* onNavigate closes the mobile sidebar automatically */}
              <SidebarNav sidebarCollapsed={false} onNavigate={() => setMobileOpen(false)} />
              <SidebarFooter />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Main content area ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="h-14 bg-white/80 backdrop-blur-xl border-b border-gray-200/80 flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Open mobile menu"
              aria-expanded={mobileOpen}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-5 h-5 text-gray-600" aria-hidden="true" />
            </button>

            {/* Search */}
            <div className="relative">
              {searchOpen ? (
                <div className="flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2">
                  <Search className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  <input
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search modules…"
                    aria-label="Search admin modules"
                    className="bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none w-48"
                  />
                  <button
                    onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                    aria-label="Close search"
                  >
                    <X className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  aria-label="Open search"
                  className="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 rounded-xl px-3 py-2 hover:bg-gray-200 transition-colors"
                >
                  <Search className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline text-[13px]">Search…</span>
                </button>
              )}

              {/* Search results dropdown */}
              {searchOpen && searchQuery && (
                <div
                  className="absolute top-full mt-2 left-0 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50"
                  role="listbox"
                  aria-label="Search results"
                >
                  {filteredSearch.slice(0, 6).map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        role="option"
                        aria-selected={false}
                        onClick={() => {
                          navigate(item.path);
                          setSearchOpen(false);
                          setSearchQuery('');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700 transition-colors"
                      >
                        <Icon className="w-4 h-4 text-gray-400" aria-hidden="true" />
                        {item.label}
                      </button>
                    );
                  })}
                  {filteredSearch.length === 0 && (
                    <p className="text-center text-sm text-gray-400 py-3">No results found</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* AI Assistant quick access */}
            <NavLink
              to="/admin/ai-assistant"
              className={({ isActive }) =>
                `hidden sm:flex items-center gap-1.5 text-xs font-medium border px-3 py-1.5 rounded-xl hover:shadow-sm transition-all ${
                  isActive
                    ? 'bg-violet-100 text-violet-800 border-violet-300'
                    : 'bg-gradient-to-r from-violet-50 to-indigo-50 text-violet-700 border-violet-200'
                }`
              }
            >
              <Bot className="w-3.5 h-3.5" aria-hidden="true" />
              AI Assistant
            </NavLink>

            <NavLink to="/admin/notifications">
              {({ isActive }) => (
                <button
                  className={`relative p-2 rounded-xl transition-colors ${
                    isActive ? 'bg-rose-50 text-rose-600' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  aria-label="View notifications"
                >
                  <Bell className="w-4 h-4" aria-hidden="true" />
                  <span
                    className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"
                    aria-label="New notifications"
                  />
                </button>
              )}
            </NavLink>

            <div
              className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#7FA86A] to-[#96BE82] flex items-center justify-center text-xs font-bold text-white"
              aria-label={`Logged in as ${currentUser || 'Admin'}`}
              title={currentUser || 'Admin'}
            >
              {(currentUser || 'A').charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        {/* ── Scrollable content ── */}
        <main
          ref={mainRef}
          className="flex-1 overflow-y-auto overflow-x-hidden"
          id="main-content"
          aria-label="Main content"
        >
          <div className="p-4 sm:p-6">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
